import { useCallback, useEffect, useState } from 'react'
import {
  getSession,
  logout as logoutRequest,
  onUnauthorized,
  signInWithToken,
  UnauthorizedError,
} from '@/lib/api'
import type { SessionInfo } from '@/types/liveview'

type SessionState =
  /** Asking the API who we are; the app shows nothing yet either way. */
  | { status: 'loading'; session: null; notice: null }
  /** `notice` is why a handed-over token did not sign anybody in, if it didn't. */
  | { status: 'signed-out'; session: null; notice: string | null }
  | { status: 'signed-in'; session: SessionInfo; notice: null }

/**
 * Lifts `?token=` off the page's URL, and returns it.
 *
 * The Proximi.io portal embeds LiveView the way it embeds MapTap: an iframe
 * whose `src` carries the signed-in user's platform token. That token is a
 * never-expiring, organisation-wide write credential, so it is taken off the
 * URL before anything else happens — every other parameter and the hash kept,
 * as MapTap does — so it is not left in the history or sent on as a referrer.
 */
function takeTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if (token === null) return null
  params.delete('token')
  const cleaned = params.toString()
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + (cleaned ? `?${cleaned}` : '') + window.location.hash,
  )
  return token.trim() || null
}

/**
 * The one token exchange this page load may make, shared by whoever asks.
 *
 * Module-level on purpose: StrictMode runs the session effect twice, and the
 * second run finds the URL already clean. Both runs get the same promise, and
 * once it has settled the page is back to asking about its cookie like any
 * other load.
 */
let handoff: Promise<SessionInfo> | null | undefined

function tokenHandoff(): Promise<SessionInfo> | null {
  if (handoff === undefined) {
    const token = takeTokenFromUrl()
    handoff = token ? signInWithToken(token) : null
    handoff?.finally(() => {
      handoff = null
    }).catch(() => {})
  }
  return handoff
}

/**
 * What to say when the handed-over token was refused. MapTap's words where
 * there are some; the API's own sentence for a token it will not take (an
 * application token, say), because that is the one an operator can act on.
 */
function handoffMessage(error: unknown): string {
  if (error instanceof UnauthorizedError) return 'Invalid token'
  const message = error instanceof Error ? error.message : ''
  const status = Number(message.match(/^(\d{3})/)?.[1])
  if (status === 429) return 'Too many attempts, please wait a moment and try again'
  if (status >= 500) return 'Server error, please try again later'
  if (Number.isNaN(status)) return 'Network error, check your connection'
  try {
    const detail = JSON.parse(message.slice(message.indexOf('{'))) as { error?: unknown }
    if (typeof detail.error === 'string' && detail.error) return `Could not sign in: ${detail.error}`
  } catch {
    // Not a body we wrote; the generic sentence will do.
  }
  return 'Could not sign in with that token'
}

/**
 * Who is signed in, if anyone.
 *
 * The session itself lives in an `HttpOnly` cookie the page cannot read, so
 * this asks the API on load rather than inspecting storage — unless the page
 * was opened with `?token=`, in which case that token is traded for a session
 * first. `signOut` is also what every `401` anywhere in the app routes into:
 * one 401, one login screen.
 */
export function useSession() {
  const [state, setState] = useState<SessionState>({
    status: 'loading',
    session: null,
    notice: null,
  })

  const refresh = useCallback(() => {
    let cancelled = false
    setState((prev) =>
      prev.status === 'loading' ? prev : { status: 'loading', session: null, notice: null },
    )

    const handed = tokenHandoff()
    if (handed) {
      handed
        .then((session) => {
          if (!cancelled) setState({ status: 'signed-in', session, notice: null })
        })
        .catch((error: unknown) => {
          // The ordinary login screen, with the reason — not a blank page and
          // not a silently different organisation's cookie from before.
          if (!cancelled)
            setState({ status: 'signed-out', session: null, notice: handoffMessage(error) })
        })
      return () => {
        cancelled = true
      }
    }

    getSession()
      .then((session) => {
        if (!cancelled) setState({ status: 'signed-in', session, notice: null })
      })
      .catch(() => {
        // A 401 is the expected answer here, and a network failure has the same
        // remedy: show the login screen and let them try.
        if (!cancelled) setState({ status: 'signed-out', session: null, notice: null })
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => refresh(), [refresh])

  /** Called after a successful login, so the app does not re-ask the API. */
  const signedIn = useCallback((session: SessionInfo) => {
    setState({ status: 'signed-in', session, notice: null })
  }, [])

  /** Called on any 401, and by the logout button. */
  const signedOut = useCallback(() => {
    // A 401 while a token is being exchanged is that exchange's own answer;
    // leave the state to its handler, which knows to say why.
    if (handoff) return
    setState((prev) =>
      prev.status === 'signed-out' ? prev : { status: 'signed-out', session: null, notice: null },
    )
  }, [])

  // One 401 anywhere — a map tile, a delete, the snapshot poll — is the session
  // being gone, and the answer is always the login screen.
  useEffect(() => {
    onUnauthorized(signedOut)
    return () => onUnauthorized(null)
  }, [signedOut])

  const logout = useCallback(() => {
    // The cookie is cleared server-side; a failure there still means this tab
    // should stop pretending to be signed in.
    logoutRequest()
      .catch(() => {})
      .finally(() => setState({ status: 'signed-out', session: null, notice: null }))
  }, [])

  return { ...state, signedIn, signedOut, logout, refresh }
}

/** True when a rejected promise means "the session is gone". */
export const isUnauthorized = (error: unknown): boolean => error instanceof UnauthorizedError
